/**
 * Candidate slot generation and weighted scoring.
 *
 * The solver never decides anything by itself: it asks this module "what could
 * this person do?" and "how good would that be?", then takes the best answer.
 * Keeping the judgement here means the whole of Lunchly's opinion about lunch
 * times is one readable list of nine terms that a manager can re-weight from the
 * Settings panel without anybody touching the search algorithm.
 *
 * Three ideas run through the file:
 *
 *  1. **Feasibility is not a score.** A slot that breaks a hard rule is not
 *     "very negative", it is impossible. Hard rules produce `blockers` and a
 *     `-Infinity` total, and no weight can trade them away. The terms are still
 *     computed for an infeasible candidate so the explain panel can say *how
 *     close* it was and *what* stopped it.
 *  2. **Every term is normalised to roughly -1…+1 before its weight applies.**
 *     Otherwise a weight of `2` on coverage would mean something completely
 *     different from a weight of `2` on fairness and the Settings sliders would
 *     be meaningless.
 *  3. **Every term writes its own sentence.** `note` is finished prose with the
 *     real numbers in it, because the explain panel must never re-derive the
 *     reasoning and get the wording subtly different from the maths.
 *
 * Rejected slots are first-class. `buildCandidates` deliberately returns slots
 * the entry may *not* use - anything inside the group window - tagged with the
 * rule they break, so the solver can report why the obvious nearer slot was not
 * taken instead of silently offering a later one.
 *
 * Pure logic: no DOM, no storage, nothing handed in is mutated.
 */

import {
  DEFAULT_SCORING_WEIGHTS,
  DEFAULT_SETTINGS,
  EVENING_SINGLE_LUNCH_FROM_MINUTES,
  LUNCH_GROUPS,
  NISCC_SINGLE_LUNCH_CUTOFF_MINUTES,
  maxSimultaneousLunchesAt,
  resolveDayConfig,
} from '../core/settings.js';
import {
  DAY_LABELS,
  MINUTES_PER_DAY,
  formatDuration,
  formatMinutes,
  parseTimeToMinutes,
  slotsBetween,
} from '../core/time.js';
import { HISTORICAL_DAY_SLOTS, HISTORICAL_EVENING_SLOTS } from '../data/benchmark.js';
import { lunchesAt, staffOnDutyAt } from './coverage.js';

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * What a fixed shift lunch is worth at its own time.
 *
 * Larger than any achievable sum of the weighted terms, so the outcome of
 * `settings.mandatoryShiftLunches` cannot be changed by scoring weights. The
 * solver's day-level penalties are of the order of tens; this is far above them.
 */
const MANDATORY_SLOT_BONUS = 10000;

/** Hard-violation codes carried on a Candidate. */
const VIOLATION_BEFORE_EARLIEST = 'before-earliest';
const VIOLATION_AFTER_LATEST = 'after-latest';
const VIOLATION_OUTSIDE_SHIFT = 'outside-shift';
const VIOLATION_OUTSIDE_WINDOW = 'outside-window';

/**
 * The only two rules the solver may switch off, named here because both this
 * module and `solver.js` have to agree on the spelling. An entry carrying
 * `relaxations: ['lunch-window']` is a solver-built copy, never a stored one.
 */
const RELAX_WINDOW = 'lunch-window';
const RELAX_SPREAD = 'preferred-spread';

/**
 * Normalisers. Each one exists to stop a term drowning the other eight; the
 * comment on each says what "full marks" is supposed to mean.
 */

/** Never let the preferred-time term be sharper than ±90 minutes. */
const PREFERRED_HALF_SPAN_FLOOR = 90;
/** How far outside the window counts as "completely wrong" for windowFit. */
const WINDOW_TOLERANCE_FLOOR = 60;
/** Spare cover worth full marks, as a share of the day's staff. */
const COVERAGE_HEADROOM_SHARE = 0.25;
/** Good slots in a week - the scale the fairness counter is read against. */
const FAIRNESS_SCALE = 5;
/**
 * `allowLaterWhenBusy: false` is an employee promise, not a company rule, so it
 * cannot make a slot infeasible - but it has to outweigh an ordinary term or the
 * promise is worthless. Deliberately outside -1…+1; the one exception in the file.
 */
const NO_LATER_PENALTY = -2;

/** Historical clock times per group, pre-parsed into minutes. */
const HISTORICAL_SLOTS = {
  [LUNCH_GROUPS.DAY]: new Set(HISTORICAL_DAY_SLOTS.map((time) => parseTimeToMinutes(time))),
  [LUNCH_GROUPS.EVENING]: new Set(HISTORICAL_EVENING_SLOTS.map((time) => parseTimeToMinutes(time))),
  [LUNCH_GROUPS.NIGHT]: new Set(),
};

/**
 * Ordered metadata for the nine scoring terms.
 *
 * The Settings panel renders its weight sliders straight off this array and the
 * explain panel labels its factors from it, so the order here is the order a
 * manager sees everywhere. Keys must stay in step with `DEFAULT_SCORING_WEIGHTS`
 * - a term with no matching weight silently scores nothing.
 */
export const SCORE_TERMS = Object.freeze([
  {
    key: 'preferredTime',
    label: 'Preferred time',
    description:
      'How close the slot is to the ideal lunch point for this shift - the employee\'s own '
      + 'preferred time when they have one, otherwise the shift start plus the day profile offset.',
  },
  {
    key: 'windowFit',
    label: 'Window fit',
    description:
      'Whether the slot lands inside the day or evening lunch window that this shift\'s group uses.',
  },
  {
    key: 'coverage',
    label: 'Coverage',
    description:
      'How much headroom is left above the minimum staffing floor for the whole of the break.',
  },
  {
    key: 'simultaneous',
    label: 'Shared slot',
    description:
      'How full the slot already is relative to the day\'s cap on simultaneous lunches.',
  },
  {
    key: 'startOrder',
    label: 'Start order',
    description:
      'Keeps earlier starters on earlier lunches by correlating shift rank with slot rank, '
      + 'ranking two people who started together by who finishes first.',
  },
  {
    key: 'employeePreference',
    label: 'Employee preference',
    description:
      'The employee\'s own early / balanced / late bias, and whether they agreed to be pushed '
      + 'later when the day is busy.',
  },
  {
    key: 'spread',
    label: 'Back-to-back run',
    description:
      'Keeps each lunch group in consecutive 30-minute slots without avoidable holes in the run.',
  },
  {
    key: 'fairness',
    label: 'Fairness',
    description:
      'Balances who has been getting the well-placed slots, so the same people do not always '
      + 'take 12:00.',
  },
  {
    key: 'history',
    label: 'Historical pattern',
    description:
      'A small bonus for clock times this team has historically used for this lunch group.',
  },
]);

/* -------------------------------------------------------------------------- */
/* Candidates                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Every slot this entry could plausibly be discussed at, on the settings grid.
 *
 * The list is the union of two ranges:
 *
 *  - `earliestMinutes … latestMinutes`, the slots the entry may actually use;
 *  - the whole group window, so that a slot the entry is *not* allowed to take
 *    still appears, tagged with the rule it breaks.
 *
 * That second range is the point of this function. Without it the solver could
 * only ever say "you got 13:00"; with it, it can say "12:30 was ruled out
 * because the break would run past the end of your shift". Crossed bounds
 * (`earliest > latest`, a real conflict eligibility deliberately leaves visible)
 * yield no usable slots at all and every window slot comes back flagged - 
 * exactly the diagnosis the manager needs.
 *
 * @param {object} entry An EligibilityResult. A solver-built copy may carry
 *   `relaxations: string[]` naming soft rules that have been switched off.
 * @param {object} context The DayContext; only `settings` is read here.
 * @returns {{ slotMinutes: number, withinWindow: boolean, hardViolations: string[] }[]}
 *   Ascending by time, one entry per distinct slot.
 */
export function buildCandidates(entry, context) {
  const settings = context?.settings ?? DEFAULT_SETTINGS;
  const interval = positive(settings.slotIntervalMinutes, DEFAULT_SETTINGS.slotIntervalMinutes);
  const lunchMinutes = positive(settings.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);

  const window = entry?.window ?? { start: 0, end: 0 };
  const earliest = numberOr(entry?.earliestMinutes, window.start);
  const latest = numberOr(entry?.latestMinutes, window.end);
  const shift = entry?.shift?.kind === 'shift' ? entry.shift : null;
  const windowRelaxed = relaxationsOf(entry).includes(RELAX_WINDOW);

  const slots = new Set([
    // Empty when the guard rails contradict each other - that is intentional.
    ...slotsBetween(earliest, latest, interval),
    ...slotsBetween(window.start, window.end, interval),
  ]);

  return [...slots]
    .sort((a, b) => a - b)
    .map((slotMinutes) => {
      const hardViolations = [];
      if (slotMinutes < earliest) hardViolations.push(VIOLATION_BEFORE_EARLIEST);
      if (slotMinutes > latest) hardViolations.push(VIOLATION_AFTER_LATEST);
      if (shift && (slotMinutes < shift.startMinutes || slotMinutes + lunchMinutes > shift.endMinutes)) {
        hardViolations.push(VIOLATION_OUTSIDE_SHIFT);
      }
      const withinWindow = slotMinutes >= window.start && slotMinutes <= window.end;
      // A relaxed entry still reports `withinWindow: false` - the window becomes
      // a soft preference, not a fiction.
      if (!withinWindow && !windowRelaxed) hardViolations.push(VIOLATION_OUTSIDE_WINDOW);
      return { slotMinutes, withinWindow, hardViolations };
    });
}

/* -------------------------------------------------------------------------- */
/* Scoring                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Score one candidate slot for one entry against the solver's current state.
 *
 * Hard rules are evaluated first and are absolute: a candidate that breaks one
 * comes back `feasible: false` with a `-Infinity` total no matter how the
 * weights are set. The nine soft terms are still computed and returned, because
 * "13:00 would have been perfect but the floor team would have dropped to one"
 * is the sentence the manager actually needs.
 *
 * Cover is measured live against `context.working` and the *current* `state`,
 * sampled across the whole break rather than at its first minute - a 45-minute
 * lunch on a 30-minute grid straddles two buckets and the person is missing from
 * both. Nothing here is cached between calls except the per-instant head count,
 * which depends only on the rota and therefore cannot go stale.
 *
 * @param {object} entry An EligibilityResult (possibly a relaxed solver copy).
 * @param {object} candidate One item from {@link buildCandidates}.
 * @param {object} context The DayContext: settings, dayConfig, working, entries.
 * @param {object} state Solver state: `{ occupancy, assigned, coverageCache, fairness }`.
 * @returns {{ total: number, feasible: boolean, terms: object[], blockers: string[] }}
 */
export function scoreCandidate(entry, candidate, context, state) {
  const settings = context?.settings ?? DEFAULT_SETTINGS;
  const dayConfig = context?.dayConfig ?? resolveDayConfig(settings, entry?.dayKey);
  const weights = { ...DEFAULT_SCORING_WEIGHTS, ...(settings.scoringWeights ?? {}) };
  const interval = positive(settings.slotIntervalMinutes, DEFAULT_SETTINGS.slotIntervalMinutes);
  const lunchMinutes = positive(settings.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);

  const slot = numberOr(candidate?.slotMinutes, 0);
  const working = workingOf(context);
  const assigned = assignedOf(state);
  const others = assigned.filter((item) => item.employeeId !== entry?.employeeId);
  const cover = measureCover(slot, {
    lunchMinutes, interval, working, others, state, dayConfig,
  });

  const blockers = collectBlockers({
    entry, candidate, slot, dayConfig, cover, assigned, lunchMinutes, settings,
  });

  const terms = [
    termPreferredTime(entry, slot, weights),
    termWindowFit(entry, candidate, slot, weights),
    termCoverage(cover, dayConfig, working, weights),
    termSimultaneous(cover, dayConfig, slot, weights, entry),
    termStartOrder(entry, slot, context, state, weights),
    termEmployeePreference(entry, slot, weights),
    termSpread(entry, slot, interval, state, weights),
    termFairness(entry, slot, context, state, weights),
    termHistory(entry, slot, settings, weights),
  ];

  const feasible = blockers.length === 0;
  // Rounded so that two arrangements which are genuinely equal compare equal:
  // raw floating-point sums differ in the fifteenth decimal and would make the
  // local search chase noise it can never actually improve on.
  const weighted = round(terms.reduce((sum, item) => sum + item.weighted, 0), 6);

  /*
   * A fixed shift lunch is an operational instruction, not a preference, so it
   * must not be reachable by tuning. Adding a decisive bonus at its own time
   * puts it beyond the sum of every weighted term, which means no combination of
   * scoring weights - including a hand-edited or long-saved set - can slide the
   * person somewhere else.
   *
   * This is deliberately a score, not a hard rule: if 16:00 is genuinely
   * unusable (outside the shift, or it would breach the minimum staffing) the
   * candidate is infeasible above and the person still gets a lunch elsewhere
   * rather than losing one.
   */
  const mandatoryHere = Number.isFinite(entry?.mandatoryMinutes)
    && slot === entry.mandatoryMinutes;
  const total = feasible
    ? weighted + (mandatoryHere ? MANDATORY_SLOT_BONUS : 0)
    : Number.NEGATIVE_INFINITY;

  return { total, feasible, terms, blockers };
}

/* -------------------------------------------------------------------------- */
/* Hard rules                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Turn every broken hard rule into a finished sentence containing its numbers.
 *
 * Blockers are prose rather than codes on purpose: they are quoted verbatim by
 * `explain.js` ("…12:30 already has 2 people at lunch, the most Monday allows…")
 * and shown beside unassigned staff, and a code would force each consumer to
 * rebuild the wording - and the numbers - for itself.
 */
function collectBlockers({ entry, candidate, slot, dayConfig, cover, assigned, lunchMinutes, settings }) {
  const blockers = [];
  const at = formatMinutes(slot);
  const violations = candidate?.hardViolations ?? [];
  const shift = entry?.shift?.kind === 'shift' ? entry.shift : null;

  if (violations.includes(VIOLATION_BEFORE_EARLIEST)) {
    blockers.push(entry?.rule?.earliestLunchTime
      ? `${at} is before ${formatMinutes(entry?.earliestMinutes)}, the earliest start allowed by `
        + `this employee's confirmed lunch-time rule`
      : `${at} is before ${formatMinutes(entry?.earliestMinutes)}, the earliest this break may start `
        + `(${formatDuration(guardAfterStart(entry, dayConfig))} after the shift begins)`);
  }
  if (violations.includes(VIOLATION_AFTER_LATEST)) {
    blockers.push(entry?.rule?.latestLunchTime
      ? `${at} is after ${formatMinutes(entry?.latestMinutes)}, the latest start allowed by this `
        + `employee's confirmed lunch-time rule`
      : `${at} is after ${formatMinutes(entry?.latestMinutes)}, the latest this break may start and `
        + `still leave ${formatDuration(guardBeforeEnd(entry, dayConfig))} of the shift after it`);
  }
  if (violations.includes(VIOLATION_OUTSIDE_SHIFT) && shift) {
    blockers.push(
      `a ${formatDuration(lunchMinutes)} break starting at ${at} would not fit inside the `
      + `${formatMinutes(shift.startMinutes)}-${formatMinutes(shift.endMinutes)} shift`,
    );
  }
  if (violations.includes(VIOLATION_OUTSIDE_WINDOW)) {
    blockers.push(
      `${at} is outside the ${formatMinutes(entry?.window?.start)}-${formatMinutes(entry?.window?.end)} `
      + `${groupLabel(entry?.group)} lunch window`,
    );
  }

  // One lunch per person: the same body cannot be in two places, whether the
  // clash is the identical slot or an overlapping longer break.
  const clash = assigned.find(
    (item) => item.employeeId === entry?.employeeId
      && Number.isFinite(item.slotMinutes)
      && item.slotMinutes < slot + lunchMinutes
      && slot < item.slotMinutes + lunchDurationOf(settings),
  );
  if (clash) {
    blockers.push(
      `${firstWord(entry?.name)} is already booked for lunch at ${formatMinutes(clash.slotMinutes)}`,
    );
  }

  /*
   * A fixed shift lunch (settings.mandatoryShiftLunches) beats the cap on
   * simultaneous lunches at its own time. Three people on 13:00-21:00 shifts all
   * eat at 16:00 even though the day allows two at once, because sliding the
   * third to 15:30 is precisely the outcome the rule exists to prevent.
   *
   * Only this cap is waived. Everything else in this function still applies:
   * the lunch must fit inside the shift, nobody gets two, and the minimum staff
   * on duty is checked below exactly as before.
   */
  const capWaived = settings?.mandatoryLunchOverridesSimultaneousCap !== false
    && Number.isFinite(entry?.mandatoryMinutes)
    && slot === entry.mandatoryMinutes
    // The evening single-lunch rule is stricter than any fixed shift time and
    // is never waived: after 17:00 the team is too thin to lose two at once.
    && slot < EVENING_SINGLE_LUNCH_FROM_MINUTES;

  const cap = cover.simultaneousCap;
  const simultaneousOnLunch = cover.simultaneousOnLunch;
  if (!capWaived && simultaneousOnLunch > cap) {
    const nisccLimit = dayConfig?.isWeekend !== true
      && cover.simultaneousInstant < NISCC_SINGLE_LUNCH_CUTOFF_MINUTES;
    const eveningLimit = cover.simultaneousInstant >= EVENING_SINGLE_LUNCH_FROM_MINUTES;
    let reason;
    if (eveningLimit) reason = 'and nobody shares a lunch slot from 17:00 onwards';
    else if (nisccLimit) reason = 'but the NISCC weekday limit before 13:00 is one';
    else reason = `above the ${cap} ${dayLabel(entry?.dayKey)} allows`;
    blockers.push(
      `${formatMinutes(cover.simultaneousInstant)} would have ${simultaneousOnLunch} people at lunch at once, `
      + reason,
    );
  }

  const floor = Math.max(0, Math.round(Number(dayConfig?.minStaffOnDuty) || 0));
  if (cover.minRemaining < floor) {
    blockers.push(
      `a break at ${at} would drop cover to ${cover.minRemaining} at `
      + `${formatMinutes(cover.worstInstant)}, below the minimum of ${floor}`,
    );
  }

  return blockers;
}

/**
 * Sample the whole break rather than its first minute.
 *
 * Returns the worst point of the break for each limit, plus the instant it
 * happens at, so a blocker can name the exact time that fails instead of
 * blaming the slot start for something that goes wrong 20 minutes later.
 */
function measureCover(slot, { lunchMinutes, interval, working, others, state, dayConfig }) {
  const step = Math.max(1, Math.min(interval, lunchMinutes));
  const cache = state?.coverageCache instanceof Map ? state.coverageCache : null;

  let minRemaining = Number.POSITIVE_INFINITY;
  let worstInstant = slot;
  let peakOnLunch = 0;
  let busiestInstant = slot;
  let simultaneousCap = maxSimultaneousLunchesAt(dayConfig, slot);
  let simultaneousOnLunch = 1;
  let simultaneousInstant = slot;
  let worstSimultaneousExcess = Number.NEGATIVE_INFINITY;
  let fullestShare = 0;
  let minOnDuty = Number.POSITIVE_INFINITY;

  // Every bucket the break touches, plus its final minute so a shift ending
  // part-way through the break cannot slip past the sampling.
  const last = slot + lunchMinutes - 1;
  const instants = [];
  for (let instant = slot; instant < slot + lunchMinutes; instant += step) instants.push(instant);
  if (instants[instants.length - 1] !== last) instants.push(last);

  for (const moment of instants) {
    const onDuty = headCount(working, moment, cache);
    // `others` already excludes this employee, so +1 is the break being scored.
    const onLunch = lunchesAt(others, moment, lunchMinutes).length + 1;
    const remaining = onDuty - onLunch;
    const cap = maxSimultaneousLunchesAt(dayConfig, moment);
    const excess = onLunch - cap;
    const share = onLunch / cap;

    if (remaining < minRemaining) {
      minRemaining = remaining;
      worstInstant = moment;
    }
    if (onLunch > peakOnLunch) {
      peakOnLunch = onLunch;
      busiestInstant = moment;
    }
    if (excess > worstSimultaneousExcess
      || (excess === worstSimultaneousExcess && share > fullestShare)) {
      worstSimultaneousExcess = excess;
      fullestShare = share;
      simultaneousCap = cap;
      simultaneousOnLunch = onLunch;
      simultaneousInstant = moment;
    }
    if (onDuty < minOnDuty) minOnDuty = onDuty;
  }

  return {
    minRemaining: Number.isFinite(minRemaining) ? minRemaining : 0,
    worstInstant,
    peakOnLunch: Math.max(1, peakOnLunch),
    busiestInstant,
    simultaneousCap,
    simultaneousOnLunch,
    simultaneousInstant,
    minOnDuty: Number.isFinite(minOnDuty) ? minOnDuty : 0,
  };
}

/**
 * Head count at one instant, memoised.
 *
 * Safe to cache because it depends only on the rota, which does not change
 * during a solve - unlike lunch occupancy, which must never be cached.
 */
function headCount(working, minutes, cache) {
  if (!cache) return staffOnDutyAt(working, minutes);
  const hit = cache.get(minutes);
  if (hit !== undefined) return hit;
  const value = staffOnDutyAt(working, minutes);
  cache.set(minutes, value);
  return value;
}

/* -------------------------------------------------------------------------- */
/* The nine terms                                                             */
/* -------------------------------------------------------------------------- */

/** Closeness to the ideal lunch point/range, normalised over half the window. */
function termPreferredTime(entry, slot, weights) {
  const preferred = numberOr(entry?.preferredMinutes, slot);
  const rangeStart = numberOr(entry?.preferredRangeStartMinutes, preferred);
  const rangeEnd = numberOr(entry?.preferredRangeEndMinutes, preferred);
  const distance = slot < rangeStart
    ? rangeStart - slot
    : (slot > rangeEnd ? slot - rangeEnd : 0);
  const halfSpan = preferredHalfSpan(entry);
  const raw = clamp(1 - distance / halfSpan, -1, 1);
  const isRange = rangeStart !== rangeEnd;
  const preferredLabel = isRange
    ? `preferred ${formatMinutes(rangeStart)}-${formatMinutes(rangeEnd)} range`
    : `preferred lunch point of ${formatMinutes(preferred)}`;
  const note = distance === 0
    ? (isRange
      ? `${formatMinutes(slot)} is inside the ${preferredLabel}.`
      : `${formatMinutes(slot)} is exactly the ${preferredLabel}.`)
    : `${formatMinutes(slot)} is ${formatDuration(distance)} from the ${preferredLabel}.`;
  return term('preferredTime', raw, weights, note);
}

/** Inside the group window is full marks; outside falls away to -1. */
function termWindowFit(entry, candidate, slot, weights) {
  const window = entry?.window ?? { start: slot, end: slot };
  const inside = candidate?.withinWindow ?? (slot >= window.start && slot <= window.end);
  const label = `${formatMinutes(window.start)}-${formatMinutes(window.end)} ${groupLabel(entry?.group)} window`;

  if (inside) {
    return term('windowFit', 1, weights, `${formatMinutes(slot)} sits inside the ${label}.`);
  }

  const outsideBy = slot < window.start ? window.start - slot : slot - window.end;
  const tolerance = Math.max(WINDOW_TOLERANCE_FLOOR, windowSpan(window) / 2);
  const raw = clamp(1 - (2 * outsideBy) / tolerance, -1, 1);
  return term(
    'windowFit',
    raw,
    weights,
    `${formatMinutes(slot)} is ${formatDuration(outsideBy)} outside the ${label}.`,
  );
}

/**
 * Headroom above the staffing floor.
 *
 * Normalised against a quarter of the day's staff so the term saturates once
 * cover is comfortable - the difference between nine and ten spare bodies must
 * not be allowed to decide anybody's lunch - while still grading finely in the
 * danger zone just above the floor, which is the only place it should be
 * changing minds. Without that saturation an enormous, irrelevant surplus in the
 * middle of the day quietly outvotes every other term.
 */
function termCoverage(cover, dayConfig, working, weights) {
  const floor = Math.max(0, Math.round(Number(dayConfig?.minStaffOnDuty) || 0));
  const headroom = cover.minRemaining - floor;
  const denominator = Math.max(1, working.length * COVERAGE_HEADROOM_SHARE);
  const raw = clamp(headroom / denominator, -1, 1);
  const note = headroom < 0
    ? `Cover bottoms out at ${cover.minRemaining} during the break, ${Math.abs(headroom)} under the `
      + `minimum of ${floor}.`
    : `Cover bottoms out at ${cover.minRemaining} during the break, ${headroom} clear of the minimum `
      + `of ${floor}.`;
  return term('coverage', raw, weights, note);
}

/**
 * How full the slot already is.
 *
 * Cubed rather than linear because `allowSharedSlot: true` is a decision, not a
 * grudging tolerance: the first person to join an otherwise empty slot costs
 * very little, and the cost only rises steeply as the cap fills. That curve is
 * what makes "two colleagues both eat at their ideal time" beat "one of them is
 * pushed half an hour out to sit alone", which is the pattern the hand-made
 * schedules show. With a cap of 1 an occupied slot is infeasible outright, so
 * the -1 end of this term is only ever reached by a candidate that has already
 * been ruled out on a hard rule.
 */
function termSimultaneous(cover, dayConfig, slot, weights, entry) {
  const cap = cover.simultaneousCap ?? maxSimultaneousLunchesAt(dayConfig, slot);
  const alreadyBooked = Math.max(0, (cover.simultaneousOnLunch ?? cover.peakOnLunch) - 1);
  const share = clamp(alreadyBooked / cap, 0, 1);

  /*
   * Sharing a slot with somebody who belongs there is the *desired* outcome, not
   * a compromise, so it carries no penalty when this slot sits inside the
   * employee's own ladder range.
   *
   * Two people on the same 09:00-17:00 shift should eat together at 13:30 and
   * leave the neighbouring slots free, rather than being spread one-per-slot
   * across the afternoon. Penalising every shared slot did the opposite: it left
   * 13:00 half empty with one person in it, split the pair across 13:30 and
   * 14:00, and pushed everyone behind them to the late end of their range.
   *
   * The penalty still applies at full strength when the slot is outside the
   * range — squeezing somebody in at the wrong time to fill a gap is exactly
   * what it exists to discourage.
   */
  const from = Number.isFinite(entry?.preferredRangeStartMinutes)
    ? entry.preferredRangeStartMinutes
    : entry?.preferredMinutes;
  const to = Number.isFinite(entry?.preferredRangeEndMinutes)
    ? entry.preferredRangeEndMinutes
    : entry?.preferredMinutes;
  const belongsHere = Number.isFinite(from) && Number.isFinite(to)
    && slot >= from && slot <= to;

  // A fixed shift lunch is not competing for the slot — it owns it.
  const mandatoryHere = Number.isFinite(entry?.mandatoryMinutes)
    && slot === entry.mandatoryMinutes;

  const raw = (belongsHere || mandatoryHere) ? 0 : -(share * share * share);

  let note;
  if (alreadyBooked === 0) {
    note = `${formatMinutes(slot)} has nobody else at lunch (cap ${cap}).`;
  } else if (belongsHere) {
    note = `${formatMinutes(slot)} already has ${alreadyBooked} of ${cap} lunch places taken, `
      + 'but it is inside this shift\'s own lunch range, so sharing it is not penalised.';
  } else {
    note = `${formatMinutes(slot)} already has ${alreadyBooked} of ${cap} lunch places taken.`;
  }
  return term('simultaneous', raw, weights, note);
}

/**
 * Pairwise ordering between when somebody works and when they eat.
 *
 * Each candidate is compared with colleagues already placed in the same group.
 * An earlier starter eating later is an inversion; equal lunch slots are
 * harmless. Two people who started together are ranked by who finishes first -
 * the queue in `solver.js` breaks that tie the same way, and without it here the
 * local search is free to undo it, which is how a Sunday 16:00-23:00 ended up
 * eating at 19:30 behind a 16:00-00:00 at 19:00. Only a genuinely identical
 * shift is skipped. Reading actual placements rather than preferred-time ranks
 * keeps the order intact after the compactness pass moves the run.
 */
function termStartOrder(entry, slot, context, state, weights) {
  const cohort = (context?.entries ?? []).filter(
    (item) => item?.eligible && item.group === entry?.group && item.employeeId !== entry?.employeeId,
  );
  if (cohort.length === 0) {
    return term('startOrder', 0, weights, 'Nobody else shares this lunch group, so start order does not apply.');
  }

  const placed = new Map(assignedOf(state).map((item) => [item.employeeId, item.slotMinutes]));
  let comparable = 0;
  let inversions = 0;
  for (const colleague of cohort) {
    const colleagueSlot = placed.get(colleague.employeeId);
    if (!Number.isFinite(colleagueSlot)) continue;
    const order = compareWorkOrder(colleague, entry);
    if (order === 0) continue;
    comparable += 1;
    if (order < 0 && colleagueSlot > slot) inversions += 1;
    if (order > 0 && colleagueSlot < slot) inversions += 1;
  }
  if (comparable === 0) {
    return term('startOrder', 0, weights, 'No placed colleague has a different shift to compare yet.');
  }

  const raw = clamp(1 - 2 * (inversions / comparable), -1, 1);

  return term(
    'startOrder',
    raw,
    weights,
    inversions === 0
      ? `${formatMinutes(slot)} keeps ${firstWord(entry?.name)} in shift order with ${comparable} placed `
        + `${comparable === 1 ? 'colleague' : 'colleagues'}.`
      : `${formatMinutes(slot)} would put ${firstWord(entry?.name)} out of shift order with ${inversions} of `
        + `${comparable} placed ${comparable === 1 ? 'colleague' : 'colleagues'}.`,
  );
}

/**
 * Which of two shifts should reach lunch first: negative when `a` should,
 * positive when `b` should, zero when there is nothing to choose between them.
 *
 * The earlier start goes first. Where the starts match, the earlier finish goes
 * first - somebody on a 16:00-23:00 has an hour less left to work than somebody
 * on a 16:00-00:00, so the shorter shift eats and gets back on the phones. An
 * end past midnight is stored as >1440, on the same axis as the start, so an
 * overnight shift still reads as finishing later.
 */
function compareWorkOrder(a, b) {
  const aStart = numberOr(a?.shift?.startMinutes, 0);
  const bStart = numberOr(b?.shift?.startMinutes, 0);
  if (aStart !== bStart) return aStart - bStart;
  return numberOr(a?.shift?.endMinutes, aStart) - numberOr(b?.shift?.endMinutes, bStart);
}

/**
 * The employee's own early / balanced / late bias.
 *
 * Measured against the *whole* window rather than the half-window the
 * preferred-time term uses, because this is a leaning and not a target: it
 * should settle a choice between two otherwise equal slots without being able to
 * overrule the ideal lunch point or the start order. The one exception is an
 * employee who has not agreed to be pushed later, which is a promise and is
 * scored as one.
 */
function termEmployeePreference(entry, slot, weights) {
  const rule = entry?.rule ?? {};
  const preferred = numberOr(entry?.preferredMinutes, slot);
  const biasSpan = Math.max(2 * PREFERRED_HALF_SPAN_FLOOR, windowSpan(entry?.window));
  const later = slot - preferred;

  // A refusal to be pushed later is a promise to the employee, so it outweighs
  // the ordinary bias - but it is still soft, and never blocks a placement.
  if (rule.allowLaterWhenBusy === false && later > 0) {
    return term(
      'employeePreference',
      NO_LATER_PENALTY,
      weights,
      `A staff rule says ${firstWord(entry?.name)} may not be pushed later than `
      + `${formatMinutes(preferred)}, and ${formatMinutes(slot)} is ${formatDuration(later)} after it.`,
    );
  }

  if (rule.priority === 'early') {
    const raw = clamp(-later / biasSpan, -1, 1);
    return term('employeePreference', raw, weights, biasNote('an early', entry, slot, preferred, later));
  }
  if (rule.priority === 'late') {
    const raw = clamp(later / biasSpan, -1, 1);
    return term('employeePreference', raw, weights, biasNote('a late', entry, slot, preferred, later));
  }
  return term(
    'employeePreference',
    0,
    weights,
    `${firstWord(entry?.name)} has no early or late preference set.`,
  );
}

function biasNote(bias, entry, slot, preferred, later) {
  const direction = later === 0 ? 'exactly on' : (later > 0 ? `${formatDuration(later)} after` : `${formatDuration(-later)} before`);
  return `${firstWord(entry?.name)} prefers ${bias} lunch, and ${formatMinutes(slot)} is ${direction} `
    + `the ${formatMinutes(preferred)} target.`;
}

/**
 * Spacing between lunches.
 *
 * Every empty grid slot is given the full gap penalty. Combined with the high
 * default weight and the whole-solution compactness objective, that makes one
 * continuous 30-minute run the normal result while hard feasibility still wins:
 * a shift guard, NISCC cap or staffing floor can always force a justified gap.
 *
 * Only lunches in the *same group* count. The day list and the evening list are
 * two separate runs with hours of shift between them; measuring the evening's
 * first lunch against the day's last would score every single evening slot as a
 * four-hour hole and tell the manager nothing.
 */
function termSpread(entry, slot, interval, state, weights) {
  if (relaxationsOf(entry).includes(RELAX_SPREAD)) {
    return term('spread', 0, weights, 'Spacing was relaxed so that this lunch could be placed at all.');
  }

  const occupied = occupiedSlots(state, entry?.group);
  const shared = occupied.includes(slot);
  const others = occupied.filter((value) => value !== slot);
  if (others.length === 0 && !shared) {
    return term('spread', 0, weights, 'No other lunches are booked in this group yet, so spacing is not in play.');
  }

  // Joining a slot somebody already holds changes nothing about the shape of the
  // run, so it cannot be blamed for a gap it did not open.
  const gap = shared ? 0 : largestGapCreated(slot, others, interval);
  if (gap === 0) {
    return term(
      'spread',
      0,
      weights,
      shared
        ? `${formatMinutes(slot)} is already in use, so this lunch leaves the shape of the day unchanged.`
        : `${formatMinutes(slot)} sits alongside the lunches already booked without leaving a dead slot.`,
    );
  }
  const raw = -1;
  return term(
    'spread',
    raw,
    weights,
    `Using ${formatMinutes(slot)} would leave ${gap} empty slot${gap === 1 ? '' : 's'} between it and `
    + 'the lunches either side.',
  );
}

/** Empty grid slots this placement would strand between two used ones. */
function largestGapCreated(slot, occupied, interval) {
  const before = occupied.filter((value) => value < slot);
  const after = occupied.filter((value) => value > slot);
  const gapBefore = before.length ? (slot - Math.max(...before)) / interval - 1 : 0;
  const gapAfter = after.length ? (Math.min(...after) - slot) / interval - 1 : 0;
  return Math.max(0, Math.round(Math.max(gapBefore, gapAfter)));
}

/**
 * Week-level fairness.
 *
 * `state.fairness` counts the well-placed slots each person has already had - 
 * seeded from `context.fairness` so earlier days of the same week carry over.
 *
 * "Well placed" means *exactly* the slot they wanted, not merely near it. A
 * looser test would tag every plausible slot as a favour and the term would then
 * apply equally to all of them, cancelling itself out and doing nothing. The
 * term only fires on such a slot, too: there is no fairness in penalising
 * somebody for accepting a bad time, and whoever has been below the team average
 * gets first refusal on the good ones.
 */
function termFairness(entry, slot, context, state, weights) {
  const preferred = numberOr(entry?.preferredMinutes, slot);
  const rangeStart = numberOr(entry?.preferredRangeStartMinutes, preferred);
  const rangeEnd = numberOr(entry?.preferredRangeEndMinutes, preferred);
  const counts = state?.fairness instanceof Map ? state.fairness : new Map();
  const mine = numberOr(counts.get(entry?.employeeId), 0);

  if (slot < rangeStart || slot > rangeEnd) {
    return term(
      'fairness',
      0,
      weights,
      `${formatMinutes(slot)} is outside ${firstWord(entry?.name)}'s preferred range, so fairness does not apply.`,
    );
  }

  const cohort = (context?.entries ?? []).filter((item) => item?.eligible);
  const average = cohort.length
    ? cohort.reduce((sum, item) => sum + numberOr(counts.get(item.employeeId), 0), 0) / cohort.length
    : 0;
  const raw = clamp((average - mine) / FAIRNESS_SCALE, -1, 1);

  return term(
    'fairness',
    raw,
    weights,
    `${firstWord(entry?.name)} has had ${mine} well-placed slot${mine === 1 ? '' : 's'} this week `
    + `against a team average of ${average.toFixed(1)}.`,
  );
}

/**
 * A nudge towards the clock times this team actually uses.
 *
 * Deliberately the smallest weight in the set and deliberately never negative
 * beyond zero: history is evidence about taste, not about rules, and it must
 * never be able to move a feasibility decision. Off entirely when
 * `useHistoricalHints` is false.
 */
function termHistory(entry, slot, settings, weights) {
  if (settings?.useHistoricalHints === false) {
    return term('history', 0, weights, 'Historical hints are switched off in Settings.');
  }
  const group = entry?.group ?? LUNCH_GROUPS.DAY;
  const known = HISTORICAL_SLOTS[group] ?? HISTORICAL_SLOTS[LUNCH_GROUPS.DAY];
  const clock = ((slot % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const seen = known.has(clock);
  return term(
    'history',
    seen ? 1 : 0,
    weights,
    seen
      ? `${formatMinutes(slot)} is one of the times this team historically uses for ${groupLabel(group)} lunches.`
      : `${formatMinutes(slot)} has not been used for a ${groupLabel(group)} lunch before.`,
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Assemble one TermScore, applying the configured weight. */
function term(key, raw, weights, note) {
  const meta = SCORE_TERMS.find((item) => item.key === key);
  const weight = Math.max(0, numberOr(weights[key], 0));
  return {
    key,
    label: meta?.label ?? key,
    raw: round(raw),
    weight,
    weighted: round(raw * weight),
    note,
  };
}

/** Half the window, floored so a narrow window cannot make the term hair-trigger. */
function preferredHalfSpan(entry) {
  return Math.max(PREFERRED_HALF_SPAN_FLOOR, windowSpan(entry?.window) / 2);
}

function windowSpan(window) {
  const start = numberOr(window?.start, 0);
  const end = numberOr(window?.end, 0);
  return Math.max(0, end - start);
}

/**
 * Slots holding at least one lunch in a group, ascending.
 *
 * Always sorted, never in Map insertion order: the occupancy index is rebuilt
 * constantly as the solver tries and unwinds moves, and a term that read it in
 * arrival order would make the whole solve depend on search history.
 */
function occupiedSlots(state, group) {
  const source = state?.occupancy instanceof Map
    ? [...state.occupancy.values()].flat()
    : assignedOf(state);

  const slots = new Set(
    source
      .filter((item) => Number.isFinite(item?.slotMinutes)
        && (!group || !item.group || item.group === group))
      .map((item) => item.slotMinutes),
  );
  return [...slots].sort((a, b) => a - b);
}

function assignedOf(state) {
  return Array.isArray(state?.assigned) ? state.assigned : [];
}

/** Worked shifts for the day, derived when the context did not pre-filter them. */
function workingOf(context) {
  if (Array.isArray(context?.working)) return context.working;
  return (context?.shifts ?? []).filter((shift) => shift?.kind === 'shift');
}

function relaxationsOf(entry) {
  return Array.isArray(entry?.relaxations) ? entry.relaxations : [];
}

function lunchDurationOf(settings) {
  return positive(settings?.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);
}

/** The guard rail behind `earliestMinutes`, for the blocker sentence. */
function guardAfterStart(entry, dayConfig) {
  return Number.isFinite(entry?.rule?.minMinutesAfterShiftStart)
    ? entry.rule.minMinutesAfterShiftStart
    : numberOr(dayConfig?.minMinutesAfterShiftStart, 0);
}

/** The guard rail behind `latestMinutes`, for the blocker sentence. */
function guardBeforeEnd(entry, dayConfig) {
  return Number.isFinite(entry?.rule?.minMinutesBeforeShiftEnd)
    ? entry.rule.minMinutesBeforeShiftEnd
    : numberOr(dayConfig?.minMinutesBeforeShiftEnd, 0);
}

function groupLabel(group) {
  if (group === LUNCH_GROUPS.EVENING) return 'evening';
  if (group === LUNCH_GROUPS.NIGHT) return 'night';
  return 'day';
}

function dayLabel(dayKey) {
  return DAY_LABELS[dayKey] ?? 'this day';
}

/** First name, for sentences that read better without the surname. */
function firstWord(name) {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return 'This employee';
  return trimmed.split(/\s+/)[0];
}

function clamp(value, low, high) {
  if (!Number.isFinite(value)) return low;
  return Math.min(Math.max(value, low), high);
}

function numberOr(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function positive(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Three decimals by default: enough to break ties, few enough to read. */
function round(value, decimals = 3) {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
